'use client'

import { FC, useCallback, useEffect, useState } from 'react'
import { useLocationPage } from '@/hooks/useLocationPage'
import { useSearchedEvent } from '@/hooks/useAnalytics'
import { useSearch } from '@/hooks/useSearch'
import { Panel, PanelItem, PanelLayout } from '../Panel'
import { Colour } from '../constants'
import styles from './styles.module.scss'
import cx from 'classnames'
import { Link } from '../Link'
import { Button } from '../Button'
import { useSearchableBlogPosts } from '@/hooks/useSearchableBlogPosts'

const OPTIONS = {
  minLength: 2,
  keys: ['fields.title', 'fields.subTitle', 'fields.keywords'],
}

export interface IndexableBlogPost {
  sys: {
    createdAt: string
  }
  fields: {
    title?: string | null
    subTitle?: string | null
    slug: string
    keywords?: string[] | string
    categories?: { fields: { title: string } }[]
  }
}

export const SearchBox: FC = () => {
  const { blogPosts } = useSearchableBlogPosts()
  const [searchTerm, setSearchTerm] = useState('')
  const { find, results, clearResults } = useSearch(blogPosts, OPTIONS)
  const page = useLocationPage()
  const pushSearchedEvent = useSearchedEvent(page)
  const onSearchChanged = useCallback(
    (term: string) => {
      if (term.length >= OPTIONS.minLength) {
        find(term)
      } else {
        clearResults()
      }
      setSearchTerm(term)
    },
    [find, setSearchTerm, clearResults],
  )

  useEffect(() => {
    if (results.status !== 'skipped') {
      const resultsCount = results.status === 'found' ? results.items.length : 0
      pushSearchedEvent(results.searchTerm, resultsCount)
    }
  }, [results, pushSearchedEvent])

  const showOverlay = () => searchTerm.length > OPTIONS.minLength - 1
  const handleBlur = () => {
    clearResults()
    setSearchTerm('')
  }
  const getCurrentUrl = () => `/blog/search?q=${searchTerm}`

  return (
    <Panel
      layout={PanelLayout.full}
      colour={Colour.warmStone}
      className={styles.searchBox}
      short
    >
      <div
        className={cx(styles.overlay, { [styles.shown]: showOverlay() })}
        onClick={handleBlur}
      />
      <PanelItem className={styles.searchBoxItem}>
        <div className={styles.relative}>
          <div className={styles.searchContainer}>
            <div className={styles.inputContainer}>
              <div className={styles.inputInner}>
                <input
                  aria-label="Search articles"
                  placeholder="Search articles"
                  value={searchTerm}
                  className={styles.input}
                  type="search"
                  onChange={e => onSearchChanged(e.target.value)}
                />
                <Button
                  className={styles.button}
                  url={getCurrentUrl()}
                  tabIndex={searchTerm.length <= OPTIONS.minLength ? -1 : 0}
                  disabled={searchTerm.length <= OPTIONS.minLength}
                >
                  Search
                </Button>
              </div>
              {results?.status === 'not-found' && (
                <div className={styles.results}>
                  <h5 className={styles.noResults}>
                    {`We couldn't find any results for "${results.searchTerm}". Why
                    not take a look at some of our latest articles?`}
                  </h5>
                </div>
              )}
              {results?.items.length ? (
                <div className={styles.results}>
                  {results?.items.map((post: IndexableBlogPost) => {
                    return (
                      <Link
                        className={styles.searchBoxResult}
                        key={post.fields.slug}
                        url={`/blog/${post.fields.slug}/`}
                      >
                        <p className={styles.item}>{post.fields.title}</p>
                      </Link>
                    )
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </PanelItem>
    </Panel>
  )
}
